import { CustomField } from "../../../api/CustomFields/CustomFields";
import {
  CustomFieldSearchOperator,
  CustomFieldSearchOperatorConst,
  CustomFieldSearchQuery,
  CustomFieldValuesSearchPredicate,
  CustomFieldValuesSearchPredicateConst,
  ICustomFieldSearch,
} from "../../../api/GenericTypes";
import {
  convertCustomFieldDataTypeToYupShape,
  convertNativeCustomFieldDataTypeToFormValue,
  convertFormValueToNativeCustomFieldDataType,
} from "../../CustomFields/CustomFieldUtils";
import * as yup from "yup";
import { AnySchema, lazy } from "yup";
import { CustomFieldSearchQueryForm, ICustomFieldSearchForm } from "./CustomFieldFilterBuilder";
import { yupResolver } from "@hookform/resolvers/yup";
import { CustomFieldFilterLayoutForm } from "../CustomFieldFilterLayout/CustomFieldFilterLayout";
import { UnpackNestedValue } from "react-hook-form";

// Interfaces

export type CustomFieldFilterType = "tree" | "flat";

// Conversion utils
/**
 * Converts custom field filter values to a query format based on the data type and operator.
 * @author @CorradoSurmanowicz
 * @param values - The values to be converted.
 * @param dataType - The data type of the custom field.
 * @param operator - The operator used in the custom field search query.
 * @returns The converted values in the appropriate query format.
 *
 * The function handles different data types and operators, converting the values accordingly.
 * For null operators ("IS_NULL" or "IS_NOT_NULL"), it returns null.
 * For other operators, it converts the values based on the data type and operator.
 *
 * Supported data types:
 * - String, Url, StringArray, UrlArray
 * - Integer, IntegerArray
 * - Float, FloatArray
 * - Boolean
 * - Date, DateArray
 * - DateTime, DateTimeArray
 * - Time, TimeArray
 * - TimeRange, DateTimeRange
 * - Dataset, Sample, Project, Person, Method, SharedContent, LabNotebook, LabNotebookExperiment, LabNotebookEntry, ViewableEntity, Inventory
 * - DatasetArray, SampleArray, ProjectArray, PersonArray, MethodArray, SharedContentArray, LabNotebookArray, LabNotebookExperimentArray, LabNotebookEntryArray, InventoryArray
 * - Barcode
 *
 * The function uses `convertCustomFieldFilterFormValuesToNative` to convert the values to the native data type.
 */
export const convertCustomFieldFilterFormValuesToNative = (
  values: any,
  dataType: CustomField["dataType"],
  operator: CustomFieldSearchQuery["operator"] | null
) => {
  // Catch null values as they are the same for every data type
  if (operator === "IS_NULL" || operator === "IS_NOT_NULL") {
    return null;
  }
  switch (dataType) {
    case "String":
    case "Url":
    case "Barcode":
      return convertFormValueToNativeCustomFieldDataType(values, "String");
    case "StringArray":
    case "UrlArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertFormValueToNativeCustomFieldDataType(values, "StringArray");
        case "STRING_CONTAINS":
        case "STRING_NOT_CONTAINS":
          return convertFormValueToNativeCustomFieldDataType(values, "String");
        default:
          return values;
      }
    case "Integer":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertFormValueToNativeCustomFieldDataType(values, "Integer");
        default:
          return values;
      }

    case "IntegerArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertFormValueToNativeCustomFieldDataType(values, "IntegerArray");
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertFormValueToNativeCustomFieldDataType(values, "Integer");
        default:
          return values;
      }
    case "Float":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertFormValueToNativeCustomFieldDataType(values, "Float");
        default:
          return values;
      }
    case "FloatArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertFormValueToNativeCustomFieldDataType(values, "FloatArray");
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertFormValueToNativeCustomFieldDataType(values, "Float");
        default:
          return values;
      }
    case "Boolean":
      return convertFormValueToNativeCustomFieldDataType(values, "Boolean");
    case "Date":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertFormValueToNativeCustomFieldDataType(values, "Date");
        default:
          return values;
      }
    case "DateArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertFormValueToNativeCustomFieldDataType(values, "DateArray");
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertFormValueToNativeCustomFieldDataType(values, "Date");
        default:
          return values;
      }
    case "DateTime":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertFormValueToNativeCustomFieldDataType(values, "DateTime");
        default:
          return values;
      }
    case "DateTimeArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertFormValueToNativeCustomFieldDataType(values, "DateTimeArray");
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertFormValueToNativeCustomFieldDataType(values, "DateTime");
        default:
          return values;
      }
    case "Time":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertFormValueToNativeCustomFieldDataType(values, "Time");
        default:
          return values;
      }
    case "TimeArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertFormValueToNativeCustomFieldDataType(values, "TimeArray");
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertFormValueToNativeCustomFieldDataType(values, "Time");
        default:
          return values;
      }
    case "TimeRange":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertFormValueToNativeCustomFieldDataType(values, "TimeRange");
        default:
          return values;
      }
    case "DateTimeRange":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertFormValueToNativeCustomFieldDataType(values, "DateTimeRange");
        default:
          return values;
      }
    case "Dataset":
    case "Sample":
    case "Project":
    case "Person":
    case "Method":
    case "SharedContent":
    case "LabNotebook":
    case "LabNotebookExperiment":
    case "LabNotebookEntry":
    case "Attachment":
    case "InventoryItem":
      return values;
    case "DatasetArray":
    case "SampleArray":
    case "ProjectArray":
    case "PersonArray":
    case "MethodArray":
    case "SharedContentArray":
    case "LabNotebookArray":
    case "LabNotebookExperimentArray":
    case "LabNotebookEntryArray":
    case "InventoryItemArray":
      return values;
    default:
      return values;
  }
};

/**
 * Converts custom field filter query values to a form-compatible format based on the data type and operator.
 * @author @CorradoSurmanowicz
 * @param values - The values to be converted.
 * @param dataType - The data type of the custom field. Can be one of the various custom field data types such as "String", "Integer", "Float", etc.
 * @param operator - The operator used in the custom field search query. Can be one of the various operators such as "EQUALS", "NOT_EQUALS", "GREATER_THAN", etc.
 * @returns The converted values in a form-compatible format.
 *
 * The function handles different data types and operators, converting the values accordingly.
 * For example, for "String" and "Url" data types, it converts the values to a string format.
 * For array data types like "StringArray" and "IntegerArray", it handles specific operators like "IN" and "NOT_IN" differently.
 * For null operators like "IS_NULL" and "IS_NOT_NULL", it returns null.
 *
 * The function uses `convertCustomFieldFilterNativeValuesToForm` to perform the actual conversion based on the data type.
 */
export const convertCustomFieldFilterNativeValuesToForm = (
  values: any,
  dataType: CustomField["dataType"] | null | undefined,
  operator: CustomFieldSearchQuery["operator"] | null
) => {
  // Catch null values as they are the same for every data type
  if (operator === "IS_NULL" || operator === "IS_NOT_NULL") {
    return null;
  }
  switch (dataType) {
    case "String":
    case "Url":
    case "Barcode":
      return convertNativeCustomFieldDataTypeToFormValue(values, "String");
    case "StringArray":
    case "UrlArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertNativeCustomFieldDataTypeToFormValue(values, "StringArray");
        case "STRING_CONTAINS":
        case "STRING_NOT_CONTAINS":
          return convertNativeCustomFieldDataTypeToFormValue(values, "String");
        default:
          return values;
      }
    case "Integer":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertNativeCustomFieldDataTypeToFormValue(values, "Integer");
        default:
          return values;
      }

    case "IntegerArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertNativeCustomFieldDataTypeToFormValue(values, "IntegerArray");
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertNativeCustomFieldDataTypeToFormValue(values, "Integer");
        default:
          return values;
      }
    case "Float":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertNativeCustomFieldDataTypeToFormValue(values, "Float");
        default:
          return values;
      }
    case "FloatArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertNativeCustomFieldDataTypeToFormValue(values, "FloatArray");
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertNativeCustomFieldDataTypeToFormValue(values, "Float");
        default:
          return values;
      }
    case "Boolean":
      return Boolean(values);
    // return convertNativeCustomFieldDataTypeToFormValue(values, "Boolean");
    case "Date":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertNativeCustomFieldDataTypeToFormValue(values, "Date");
        default:
          return values;
      }
    case "DateArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertNativeCustomFieldDataTypeToFormValue(values, "DateArray");
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertNativeCustomFieldDataTypeToFormValue(values, "Date");
        default:
          return values;
      }
    case "DateTime":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertNativeCustomFieldDataTypeToFormValue(values, "DateTime");
        default:
          return values;
      }
    case "DateTimeArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertNativeCustomFieldDataTypeToFormValue(values, "DateTimeArray");
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertNativeCustomFieldDataTypeToFormValue(values, "DateTime");
        default:
          return values;
      }
    case "Time":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertNativeCustomFieldDataTypeToFormValue(values, "Time");
        default:
          return values;
      }
    case "TimeArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertNativeCustomFieldDataTypeToFormValue(values, "TimeArray");
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertNativeCustomFieldDataTypeToFormValue(values, "Time");
        default:
          return values;
      }
    case "TimeRange":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertNativeCustomFieldDataTypeToFormValue(values, "TimeRange");
        default:
          return values;
      }
    case "DateTimeRange":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertNativeCustomFieldDataTypeToFormValue(values, "DateTimeRange");
        default:
          return values;
      }
    case "Dataset":
    case "Sample":
    case "Project":
    case "Person":
    case "Method":
    case "SharedContent":
    case "LabNotebook":
    case "LabNotebookExperiment":
    case "LabNotebookEntry":
    case "Attachment":
    case "InventoryItem":
      return values;
    case "DatasetArray":
    case "SampleArray":
    case "ProjectArray":
    case "PersonArray":
    case "MethodArray":
    case "SharedContentArray":
    case "LabNotebookArray":
    case "LabNotebookExperimentArray":
    case "LabNotebookEntryArray":
    case "InventoryItemArray":
      return values;
    default:
      return values;
  }
};

// Misc utils
/**
 * Recursively collects all customFieldIds from CustomFieldSearchQuery.
 * @author @CorradoSurmanowicz
 * @param filters - The filters to collect customFieldIds from.
 * @returns An array of customFieldIds.
 */
export const collectAllCustomFieldIdsFromCustomFieldSearchQuery = (
  filters: CustomFieldSearchQuery | CustomFieldSearchQueryForm | null | undefined
): number[] => {
  if (!filters) return [];
  if (!filters.predicate && !!filters.customFieldId) {
    return [filters.customFieldId];
  }
  return [
    ...collectAllCustomFieldIdsFromCustomFieldSearchQuery(filters.left),
    ...collectAllCustomFieldIdsFromCustomFieldSearchQuery(filters.right),
  ];
};

/**
 * Recursively counts all nodes in a CustomFieldSearchQuery.
 * @author @CorradoSurmanowicz
 * @param filters - The filters to count nodes from.
 * @returns The count of nodes.
 */
export const countNodesInCustomFieldSearchQuery = (
  filters: CustomFieldSearchQuery | CustomFieldSearchQueryForm | null | undefined
): number => {
  if (!filters) return 0;
  if (!filters.left && !filters.right) return 1;
  return countNodesInCustomFieldSearchQuery(filters.left) + countNodesInCustomFieldSearchQuery(filters.right);
};

/**
 * Converts a `CustomFieldSearchQueryForm` object by fetching missing custom fields and converting filter query values.
 * @author @CorradoSurmanowicz
 * @param filters - The `CustomFieldSearchQueryForm` object to be converted. Can be null.
 * @param customFieldDic - A dictionary of custom fields indexed by their IDs.
 * @param fetchCustomField - An asynchronous function to fetch a custom field by its ID.
 * @param signal - An optional `AbortSignal` to cancel the fetch request.
 * @returns A promise that resolves to the converted `CustomFieldSearchQueryForm` object.
 */
export const convertICustomFieldSearch = (
  filters: CustomFieldSearchQueryForm | null,
  customFieldDic: Record<number, CustomField>
): CustomFieldSearchQueryForm => {
  const convertNode = (node: CustomFieldSearchQueryForm): CustomFieldSearchQueryForm => {
    if (!node) return {};
    if (!node.predicate && node.customFieldId) {
      return {
        ...node,
        operator: node.operator,
        value: convertCustomFieldFilterNativeValuesToForm(
          node.value,
          customFieldDic?.[node.customFieldId]?.dataType,
          node.operator
        ),
        customField: customFieldDic?.[node.customFieldId],
        dataType: customFieldDic?.[node.customFieldId]?.dataType,
      };
    }
    return {
      ...node,
      left: convertNode(node.left!),
      right: node.right ? convertNode(node.right) : undefined,
    };
  };
  if (!filters) return {};
  return convertNode(filters);
};

/**
 * Converts CustomFieldSearchQueryForm to ICustomFieldSearch recursively.
 * @author @CorradoSurmanowicz
 * @param filters - The filters to convert.
 * @returns The converted filters.
 */
export const convertCustomFieldSearchQueryForm = (
  filters: CustomFieldSearchQueryForm | null
): CustomFieldSearchQuery | null => {
  if (!filters) return null;
  const convertNode = (node: CustomFieldSearchQueryForm): CustomFieldSearchQuery => {
    if (!node) return {};
    if (node.customField && !node.predicate) {
      const res = {
        ...node,
        customFieldId: node.customField.id,
        value: convertCustomFieldFilterFormValuesToNative(node.value, node.customField.dataType, node.operator),
      };
      delete res.customField;
      return res as CustomFieldSearchQuery;
    }

    const _node = {
      ...node,
      left: convertNode(node.left!),
      right: node.right ? convertNode(node.right) : undefined,
    };
    delete _node.customField;
    return _node as CustomFieldSearchQuery;
  };

  return convertNode(filters);
};

/**
 * Converts a `CustomFieldSearchQueryForm` object to a `CustomFieldSearchQuery` object.
 * @author @CorradoSurmanowicz
 * @param query - The `CustomFieldSearchQueryForm` object to convert. It can be `null` or `undefined`.
 * @returns The converted `CustomFieldSearchQuery` object, or `undefined` if the input query is `null` or `undefined`.
 *
 * The function performs the following conversions:
 * - If `query.customFieldId` exists, it is included in the result.
 * - If `query.operator` exists, it is included in the result.
 * - If `query.predicate` exists, it is included in the result.
 * - If `query` has a `value` property and `query.dataType` exists, the `value` is converted using `convertCustomFieldFilterValuesToQuery` and included in the result.
 * - If `query.left` exists, it is recursively converted and included in the result.
 * - If `query.right` exists, it is recursively converted and included in the result.
 */

export const convertCustomFieldSearchQueryFormToCustomFieldSearchQuery = <
  T extends CustomFieldSearchQueryForm,
  CustomFieldSearchQuery
>(
  query: T | null | undefined,
  includeDataType?: boolean,
  ignoreValueConversion: boolean = false
): CustomFieldSearchQuery => {
  if (!query) return {} as CustomFieldSearchQuery;

  const _query = {
    ...(query.customFieldId && { customFieldId: query.customFieldId }),
    ...(query.customField && { customFieldId: query.customField.id }),
    ...(query.operator && { operator: query.operator }),
    ...(query.predicate && { predicate: query.predicate }),
    ...(ignoreValueConversion
      ? Object.hasOwn(query, "value") && { value: query.value }
      : Object.hasOwn(query, "value") &&
        query.dataType && {
          value: convertCustomFieldFilterFormValuesToNative(query.value, query.dataType, query.operator),
        }),
    ...(query.left && {
      left: convertCustomFieldSearchQueryFormToCustomFieldSearchQuery(
        query.left,
        includeDataType,
        ignoreValueConversion
      ),
    }),
    ...(query.right && {
      right: convertCustomFieldSearchQueryFormToCustomFieldSearchQuery(
        query.right,
        includeDataType,
        ignoreValueConversion
      ),
    }),
    ...(includeDataType && query.dataType && { dataType: query.dataType }),
  } as CustomFieldSearchQuery;
  // console.log("Converted query", _query);
  return _query;
};

/**
 * Consolidates custom field filters into a single search query.
 * @author @CorradoSurmanowicz
 * @param filters - An array of custom field search forms, which can be undefined or null.
 * @returns An object containing the consolidated filter query.
 */
export const consolidateCustomFieldFilters = (
  filters: ICustomFieldSearchForm[] | CustomFieldSearchQuery[] | undefined | null,
  predicate: CustomFieldValuesSearchPredicate = "AND",
  includeDataType: boolean = false,
  ignoreValueConversion: boolean = false
): ICustomFieldSearch => {
  // console.log("Consolidating custom field filters", filters);
  return {
    filterQuery: convertFilterQueriesToSingleQuery(filters, predicate, includeDataType, ignoreValueConversion),
  };
};

/**
 * Recursively converts ICustomFieldSearchForm[] filterQuery to a single CustomFieldSearchQuery.
 * Injects the first filterQuery to "left" and each successive element as "right" with the predicate "AND".
 * When the elements are even, there is only a left and right side.
 * When odd, a new node is added with left containing the first element and right containing a new node with left and right.
 * @author @CorradoSurmanowicz
 * @param filters - The filters to convert.
 * @returns The consolidated CustomFieldSearchQuery.
 */
export const convertFilterQueriesToSingleQuery = (
  filters?: ICustomFieldSearchForm[] | CustomFieldSearchQuery[] | null,
  predicate: CustomFieldValuesSearchPredicate = "AND",
  includeDataType: boolean = false,
  ignoreValueConversion: boolean = false
): CustomFieldSearchQuery | null | undefined => {
  if (!filters || filters.length === 0) return null;
  const nElements = filters.length;
  if (nElements === 1)
    return convertCustomFieldSearchQueryFormToCustomFieldSearchQuery(
      Object.hasOwn(filters[0], "filterQuery")
        ? (filters[0] as ICustomFieldSearchForm).filterQuery
        : (filters[0] as CustomFieldSearchQuery),
      includeDataType,
      ignoreValueConversion
    );

  const convertNode = (index: number): CustomFieldSearchQuery | null => {
    if (index >= filters.length) return null;
    const currentFilter = Object.hasOwn(filters[index], "filterQuery")
      ? (filters[index] as ICustomFieldSearchForm).filterQuery
      : (filters[index] as CustomFieldSearchQuery);
    if (!currentFilter) return null;

    const leftNode = convertCustomFieldSearchQueryFormToCustomFieldSearchQuery(
      currentFilter,
      includeDataType,
      ignoreValueConversion
    ) as CustomFieldSearchQuery;
    if (!leftNode) return null;

    if (index === filters.length - 1) return leftNode;

    return {
      predicate: predicate,
      left: leftNode,
      right: convertNode(index + 1),
    } as CustomFieldSearchQuery;
  };
  const result = convertNode(0);
  // console.log("Consolidation result", result);
  return result;
};

// Validation utils

// This function is used to validate the form using yup.
export const yupCustomFieldFilterValidationResolver = async (
  data: ICustomFieldSearchForm,
  context: any,
  options: any,
  includeCustomField?: boolean
) => {
  return await yupResolver(
    yup.object().shape({
      id: yup.string().required("An id is required"),
      name: yup.string().required("A name is required"),
      filterQuery:
        data.type === "flat"
          ? yup.object().shape({
              customFieldId: yup.number().integer().positive().required("CustomFieldId is required"),
              ...(includeCustomField && {
                customField: yup
                  .object()
                  .shape({
                    id: yup.number().integer().positive().required("CustomFieldId is required"),
                    name: yup.string().required("CustomField name is required"),
                  })
                  .required("A valid customField is required")
                  .typeError("A valid customField is required"),
              }),
              operator: yup
                .mixed<CustomFieldSearchOperator>()
                .oneOf(CustomFieldSearchOperatorConst)
                .required("A valid operator is required")
                .typeError("A valid operator is required"),
              value: yup.mixed().nullable(),
            })
          : generateYupCustomFieldFilterTreeSchema(data.filterQuery, includeCustomField),
    })
  )(data, context, options);
};

export const customFieldFiltersSchema = yup.object().shape({
  customFieldFilters: yup.array().of(
    lazy((elem: ICustomFieldSearchForm) => {
      if (!elem) return yup.mixed().nullable();
      return yup.object().shape({
        id: yup.string().required("An id is required"),
        name: yup.string().required("A name is required"),
        filterQuery:
          elem.type === "flat"
            ? generateYupCustomFieldFilterTreeSchema(elem.filterQuery)
            : generateYupCustomFieldFilterTreeSchema(elem.filterQuery),
      });
      // if (elem.active) {
      //   return yup.object().shape({
      //     id: yup.string().required("An id is required"),
      //     name: yup.string().required("A name is required"),
      //     filterQuery:
      //       elem.type === "flat"
      //         ? generateYupCustomFieldFilterTreeSchema(elem.filterQuery)
      //         : generateYupCustomFieldFilterTreeSchema(elem.filterQuery),
      //   });
      // } else {
      //   return yup.mixed().nullable();
      // }
    })
  ),
});

// This resolver is used to validate the custom field filters form for use in the TabStoreFilterFormProvider
export const yupCustomFieldFiltersValidationResolver = async <T extends CustomFieldFilterLayoutForm>(
  data: T,
  context: any,
  options: any
) => {
  return await yupResolver(customFieldFiltersSchema)(data as UnpackNestedValue<T>, context, options);
};

/**
 * Generates a Yup schema for a custom field filter value based on the provided data type and operator.
 * @author @CorradoSurmanowicz
 * @param dataType - The data type of the custom field. It can be one of the types defined in `CustomField["dataType"]`.
 * @param operator - The operator used in the custom field search query form. It can be one of the types defined in `CustomFieldSearchQueryForm["operator"]`.
 * @param label - An optional label for the custom field.
 * @returns A Yup schema for the custom field filter value.
 *
 * The function handles various data types and operators to generate the appropriate Yup schema.
 * - For `IS_NULL` and `IS_NOT_NULL` operators, it returns a nullable mixed schema.
 * - For string and URL types, it returns a non-nullable string schema.
 * - For array types, it handles different operators and returns the appropriate schema.
 * - For numeric types (Integer, Float), it handles comparison operators and returns a non-nullable schema.
 * - For boolean, date, datetime, and time types, it handles comparison operators and returns the appropriate schema.
 * - For entity types (Dataset, Sample, Project, etc.), it returns a non-nullable schema.
 * - For unsupported or unknown data types, it returns a nullable mixed schema.
 */
const generateYupSchemaForCustomFieldFilterValue = (
  dataType: CustomField["dataType"] | null | undefined,
  operator: CustomFieldSearchQueryForm["operator"],
  label?: string
) => {
  // Catch null values as they are the same for every data type
  if (operator === "IS_NULL" || operator === "IS_NOT_NULL") {
    return yup.mixed().nullable();
  }
  switch (dataType) {
    case "String":
    case "Url":
    case "Barcode":
      return convertCustomFieldDataTypeToYupShape("String", label).nonNullable();
    case "StringArray":
    case "UrlArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertCustomFieldDataTypeToYupShape("StringArray", label);
        case "STRING_CONTAINS":
        case "STRING_NOT_CONTAINS":
          return convertCustomFieldDataTypeToYupShape("String", label);
        default:
          return yup.mixed().nullable();
      }
    case "Integer":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertCustomFieldDataTypeToYupShape("Integer", label).nonNullable();
        default:
          return yup.mixed().nullable();
      }

    case "IntegerArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertCustomFieldDataTypeToYupShape("IntegerArray", label);
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertCustomFieldDataTypeToYupShape("Integer", label).nonNullable();
        default:
          return yup.mixed().nullable();
      }
    case "Float":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertCustomFieldDataTypeToYupShape("Float", label).nonNullable();
        default:
          return yup.mixed().nullable();
      }
    case "FloatArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertCustomFieldDataTypeToYupShape("FloatArray", label);
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertCustomFieldDataTypeToYupShape("Float", label).nonNullable();
        default:
          return yup.mixed().nullable();
      }
    case "Boolean":
      return convertCustomFieldDataTypeToYupShape("Boolean", label);
    case "Date":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertCustomFieldDataTypeToYupShape("Date", label).nonNullable();
        default:
          return yup.mixed().nullable();
      }
    case "DateArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertCustomFieldDataTypeToYupShape("DateArray", label);
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertCustomFieldDataTypeToYupShape("Date", label).nonNullable();
        default:
          return yup.mixed().nullable();
      }
    case "DateTime":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertCustomFieldDataTypeToYupShape("DateTime", label).nonNullable();
        default:
          return yup.mixed().nullable();
      }
    case "DateTimeArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertCustomFieldDataTypeToYupShape("DateTimeArray", label);
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertCustomFieldDataTypeToYupShape("DateTime", label).nonNullable();
        default:
          return yup.mixed().nullable();
      }
    case "Time":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertCustomFieldDataTypeToYupShape("Time", label).nonNullable();
        default:
          return yup.mixed().nullable();
      }
    case "TimeArray":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "IN":
        case "NOT_IN":
          return convertCustomFieldDataTypeToYupShape("TimeArray", label);
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertCustomFieldDataTypeToYupShape("Time", label).nonNullable();
        default:
          return yup.mixed().nullable();
      }
    case "TimeRange":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertCustomFieldDataTypeToYupShape("TimeRange", label).nonNullable();
        default:
          return yup.mixed().nullable();
      }
    case "DateTimeRange":
      switch (operator) {
        case "EQUALS":
        case "NOT_EQUALS":
        case "GREATER_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN":
        case "LESS_THAN_OR_EQUAL":
          return convertCustomFieldDataTypeToYupShape("DateTimeRange", label).nonNullable();
        default:
          return yup.mixed().nullable();
      }
    case "Dataset":
    case "Sample":
    case "Project":
    case "Person":
    case "Method":
    case "SharedContent":
    case "LabNotebook":
    case "LabNotebookExperiment":
    case "LabNotebookEntry":
    case "Attachment":
    case "InventoryItem":
      return convertCustomFieldDataTypeToYupShape(dataType, label).nonNullable();
    case "DatasetArray":
    case "SampleArray":
    case "ProjectArray":
    case "PersonArray":
    case "MethodArray":
    case "SharedContentArray":
    case "LabNotebookArray":
    case "LabNotebookExperimentArray":
    case "LabNotebookEntryArray":
    case "InventoryItemArray":
      return convertCustomFieldDataTypeToYupShape(dataType, label).nonNullable();
    default:
      return yup.mixed().nullable();
  }
};

/**
 * Generates a Yup validation schema for custom field filters.
 * @author @CorradoSurmanowicz
 * @param dataType - The data type of the custom field. It can be a value from the `CustomField["dataType"]` type, or null, or undefined.
 * @param operator - The operator used in the custom field search query form. It should be a value from the `CustomFieldSearchQueryForm["operator"]` type.
 * @param label - An optional label for the custom field filter.
 * @returns A Yup validation schema object for custom field filters.
 */
const generateYupCustomFieldFilterSchema = (
  dataType: CustomField["dataType"] | null | undefined,
  operator: CustomFieldSearchQueryForm["operator"],
  label?: string,
  includeCustomField: boolean = false
) => {
  return yup.object().shape({
    customFieldId: yup.number().integer().positive().required("CustomFieldId is required"),
    ...(includeCustomField && {
      customField: yup
        .object()
        .shape({
          id: yup.number().integer().positive().required("CustomFieldId is required"),
          name: yup.string().required("CustomField name is required"),
        })
        .required("A valid customField is required")
        .typeError("A valid customField is required"),
    }),
    operator: yup
      .mixed<CustomFieldSearchOperator>()
      .oneOf(CustomFieldSearchOperatorConst)
      .required("A valid operator is required")
      .typeError("A valid operator is required"),
    value: generateYupSchemaForCustomFieldFilterValue(dataType, operator, label),
  });
};

/**
 * Generates a Yup validation schema for a custom field filter tree.
 * @author @CorradoSurmanowicz
 * @param filterQuery - The custom field search query form tree. It can be null or undefined.
 * @returns The Yup validation schema for the custom field filter tree.
 *
 * The function performs the following:
 * - If the tree is null or undefined, it returns a non-nullable, required Yup object schema with an error message.
 * - If the tree does not have a predicate, it generates a Yup schema for the custom field filter based on the custom field's data type, operator, and name.
 * - If the tree has a predicate, it generates a Yup object schema with the predicate, left, and right properties, recursively calling itself for the left and right subtrees.
 */
export const generateYupCustomFieldFilterTreeSchema = (
  filterQuery: CustomFieldSearchQueryForm | null | undefined,
  includeCustomField: boolean = false
): AnySchema => {
  if (!filterQuery) {
    return yup.object().shape({}).nonNullable().required("A filter or tree is required");
  }
  if (!filterQuery.predicate) {
    return generateYupCustomFieldFilterSchema(
      filterQuery.customField?.dataType ?? filterQuery.dataType,
      filterQuery.operator,
      filterQuery.customField?.name ?? "CustomField",
      includeCustomField
    );
  }
  if (filterQuery.predicate)
    return yup.object().shape({
      predicate: yup.mixed<CustomFieldValuesSearchPredicate>().oneOf(CustomFieldValuesSearchPredicateConst).required(),
      left: generateYupCustomFieldFilterTreeSchema(filterQuery.left),
      right: generateYupCustomFieldFilterTreeSchema(filterQuery.right),
    });
  return yup.object().shape({}).nonNullable().required("A filter or tree is required");
};

// /**
//  * Determines whether the given custom field search operator requires a value.
//  *
//  * @param operator - The custom field search operator to check.
//  * @returns `true` if the operator requires a value (e.g., "EQUALS", "IN", "GREATER_THAN"),
//  *          `false` if the operator does not require a value (e.g., "IS_NULL", "IS_NOT_NULL").
//  *
//  * @remarks
//  * Operators such as "IS_NULL" and "IS_NOT_NULL" do not require a value, while others do.
//  * If the operator is unrecognized, this function defaults to returning `true`.
//  */
export const isValueCustomFieldSearchOperator = (operator: CustomFieldSearchOperator): boolean => {
  switch (operator) {
    case "EQUALS":
    case "NOT_EQUALS":
    case "IN":
    case "NOT_IN":
    case "GREATER_THAN":
    case "GREATER_THAN_OR_EQUAL":
    case "LESS_THAN":
    case "LESS_THAN_OR_EQUAL":
    case "STRING_CONTAINS":
    case "STRING_NOT_CONTAINS":
      return true;
    case "IS_NULL":
    case "IS_NOT_NULL":
      return false;
    default:
      return true;
  }
};
